import axios from 'axios';
import apiClient from '../server';

const WORKER_URL = 'http://localhost:3002';

/**
 * Fetch RA city event listings (returns all events for the city in one call)
 * @param {Object} params - Parameters
 * @param {string} params.city - City name (e.g., "montreal", "toronto")
 * @param {string} [params.country="ca"] - Country code (e.g., "ca", "us", "uk")
 */
export const fetchRACityListings = async ({ city, country = 'ca' }) => {
  try {
    // Fetch page 1 to get totals
    const first = await axios.post(`${WORKER_URL}/batch/ra-city-listings`, { city, country, page: 1 });
    const { totalPages } = first.data;
    let allEvents = [...(first.data.events || [])];

    // Fetch remaining pages in parallel
    if (totalPages > 1) {
      const pages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
      const results = await Promise.all(
        pages.map(page => axios.post(`${WORKER_URL}/batch/ra-city-listings`, { city, country, page }))
      );
      results.forEach(r => allEvents.push(...(r.data.events || [])));
    }

    return { events: allEvents, totalEvents: allEvents.length };
  } catch (error) {
    console.error("Error fetching RA city listings:", error.response?.data || error);
    throw error;
  }
};

/**
 * Compare RA events with database
 * @param {Array} events - Array of events from RA
 */
export const compareRAEvents = async (events) => {
  try {
    const response = await apiClient.post('/events/compare-ra', { events });
    return response.data;
  } catch (error) {
    console.error("Error comparing RA events:", error.response?.data || error);
    throw error;
  }
};

/**
 * Scrape single RA event details
 * @param {string} url - RA event URL
 */
export const scrapeRAEvent = async (url) => {
  try {
    const response = await axios.post(`${WORKER_URL}/scrape`, { url });
    return response.data;
  } catch (error) {
    console.error("Error scraping RA event:", error.response?.data || error);
    throw error;
  }
};

// RA Cities reference data
export const RA_CITIES = {
  ca: [
    { value: 'montreal', label: 'Montreal' },
    { value: 'toronto', label: 'Toronto' },
    { value: 'vancouver', label: 'Vancouver' },
    { value: 'calgary', label: 'Calgary' },
    { value: 'ottawa', label: 'Ottawa' },
  ],
  us: [
    { value: 'newyork', label: 'New York' },
    { value: 'losangeles', label: 'Los Angeles' },
    { value: 'chicago', label: 'Chicago' },
    { value: 'miami', label: 'Miami' },
    { value: 'sanfrancisco', label: 'San Francisco' },
    { value: 'detroit', label: 'Detroit' },
    { value: 'seattle', label: 'Seattle' },
  ],
  uk: [
    { value: 'london', label: 'London' },
    { value: 'manchester', label: 'Manchester' },
    { value: 'bristol', label: 'Bristol' },
    { value: 'glasgow', label: 'Glasgow' },
    { value: 'leeds', label: 'Leeds' },
  ],
  de: [
    { value: 'berlin', label: 'Berlin' },
    { value: 'munich', label: 'Munich' },
    { value: 'frankfurt', label: 'Frankfurt' },
  ],
  es: [
    { value: 'barcelona', label: 'Barcelona' },
    { value: 'madrid', label: 'Madrid' },
    { value: 'ibiza', label: 'Ibiza' },
  ],
  nl: [
    { value: 'amsterdam', label: 'Amsterdam' },
  ],
  fr: [
    { value: 'paris', label: 'Paris' },
  ],
};

export const RA_COUNTRIES = [
  { value: 'ca', label: 'Canada' },
  { value: 'us', label: 'USA' },
  { value: 'uk', label: 'UK' },
  { value: 'de', label: 'Germany' },
  { value: 'es', label: 'Spain' },
  { value: 'nl', label: 'Netherlands' },
  { value: 'fr', label: 'France' },
];

/**
 * Fetch all skipped RA events
 * @param {number} [page=1] - Page number
 * @param {number} [limit=100] - Items per page
 */
export const getSkippedRAEvents = async (page = 1, limit = 100) => {
  try {
    const response = await apiClient.get(`/events/skip-ra?page=${page}&limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching skipped RA events:", error.response?.data || error);
    throw error;
  }
};

/**
 * Skip a single RA event
 * @param {Object} params - Parameters
 * @param {string} params.raEventId - RA event ID
 * @param {string} [params.title] - Event title
 * @param {string} [params.reason] - Reason for skipping
 */
export const skipRAEvent = async ({ raEventId, title, reason }) => {
  try {
    const response = await apiClient.post('/events/skip-ra', { raEventId, title, reason });
    return response.data;
  } catch (error) {
    console.error("Error skipping RA event:", error.response?.data || error);
    throw error;
  }
};

/**
 * Bulk skip multiple RA events
 * @param {Array} events - Array of {raEventId, title} objects
 */
export const bulkSkipRAEvents = async (events) => {
  try {
    const response = await apiClient.post('/events/skip-ra/bulk', { events });
    return response.data;
  } catch (error) {
    console.error("Error bulk skipping RA events:", error.response?.data || error);
    throw error;
  }
};

/**
 * Remove event from skip list
 * @param {string} raEventId - RA event ID to unskip
 */
export const unskipRAEvent = async (raEventId) => {
  try {
    const response = await apiClient.delete(`/events/skip-ra/${raEventId}`);
    return response.data;
  } catch (error) {
    console.error("Error unskipping RA event:", error.response?.data || error);
    throw error;
  }
};

/**
 * Re-scrape a profile's scrapSource and compare events with DB
 * @param {string} profileId - Profile ID
 */
export const rescrapeProfile = async (profileId) => {
  try {
    const response = await apiClient.post(`/scraping/rescrape-profile/${profileId}`);
    return response.data;
  } catch (error) {
    console.error("Error rescraping profile:", error.response?.data || error);
    throw error;
  }
};

/**
 * Update scrapSource URL on a profile
 * @param {string} profileId - Profile ID
 * @param {string} scrapSource - RA or Tixr club URL
 */
export const updateProfileScrapSource = async (profileId, scrapSource) => {
  try {
    const response = await apiClient.patch(`/profiles/updatesuperadmin/${profileId}`, { scrapSource });
    return response.data;
  } catch (error) {
    console.error("Error updating scrapSource:", error.response?.data || error);
    throw error;
  }
};

/**
 * Fetch events from a Tixr group page
 * @param {string} url - Tixr group URL (e.g., "https://www.tixr.com/groups/stereobar")
 */
export const fetchTixrGroupEvents = async (url) => {
  try {
    const response = await axios.post(`${WORKER_URL}/tixr/group-events`, { url });
    return response.data;
  } catch (error) {
    console.error("Error fetching Tixr group events:", error.response?.data || error);
    throw error;
  }
};

/**
 * Detect source type from a scrapSource URL
 * @param {string} url - scrapSource URL
 * @returns {'ra' | 'tixr' | 'ticketweb' | 'unknown'}
 */
export const detectSourceType = (url) => {
  if (!url) return 'unknown';
  if (url.includes('ra.co')) return 'ra';
  if (url.includes('tixr.com')) return 'tixr';
  if (url.includes('ticketweb.ca') || url.includes('ticketweb.com')) return 'ticketweb';
  return 'unknown';
};
